import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useIgnoreUnits, type GroupMember } from '@/api/groups';

const PUBLISHED_STATUS = 'published';

interface IgnoreConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerAccountId: number;
  groupKey: string;
  members: GroupMember[];
}

export function IgnoreConfirmDialog({
  open,
  onOpenChange,
  partnerAccountId,
  groupKey,
  members,
}: IgnoreConfirmDialogProps): JSX.Element {
  const { toast } = useToast();
  const ignoreMutation = useIgnoreUnits();

  const liveCount = members.filter((m) => m.publicationStatus === PUBLISHED_STATUS).length;

  const handleConfirm = () => {
    const count = members.length;
    const syncedLocationIds = members.map((m) => Number(m.id));
    ignoreMutation.mutate(
      { partnerAccountId, groupKey, syncedLocationIds },
      {
        onSuccess: () => {
          toast({
            title: 'Ignore queued',
            description: `Queued ignore for ${String(count)} unit${count === 1 ? '' : 's'}.`,
          });
          onOpenChange(false);
        },
        onError: (err) => {
          const message = err instanceof Error ? err.message : 'Failed to ignore units';
          toast({
            title: 'Error',
            description: message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="ignore-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Ignore {String(members.length)} unit{members.length === 1 ? '' : 's'}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Ignored units are hidden from publishing: they won't be published (and any that are
            currently live are removed from ScoutAround). This is reversible by un-ignoring them.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {liveCount > 0 && (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            data-testid="ignore-live-warning"
          >
            Ignoring {String(liveCount)} live unit{liveCount === 1 ? '' : 's'} will delete them from
            ScoutAround. Their public URLs will stop working; re-publishing later creates new URLs.
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={ignoreMutation.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={ignoreMutation.isPending}
            data-testid="ignore-confirm-btn"
            className={cn(buttonVariants({ variant: 'destructive' }))}
          >
            {ignoreMutation.isPending ? 'Ignoring...' : 'Yes, ignore selected'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
